import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { NotFound } from "@sal/portal-move";
import { Suspense, lazy } from "react";
import { HashRouter, Outlet, Routes, Route, useParams } from "react-router-dom";
import { PortalAuthenticatedRoute } from "@sal/onevent-portal";
import BasePage from "./pages/Layout/BasePage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import { routePermissionMapping } from "./appsettings";
import {
	NewsAndUpdatesPage,
	NewsArticleDetailPage,
	NewsArticleEditorPage
} from "@sal/onevent-portal";

const HomePage = lazy(() => import("./pages/HomePage"));
const ContainerTransportDashboardPage = lazy(() => import("./pages/ContainerTransportDashboardPage"));
const ContainerTransportImportFCLOrderPage = lazy(() => import("./pages/ContainerTransportImportFCLOrderPage"));
const ContainerTransportExportFCLOrderPage = lazy(() => import("./pages/ContainerTransportExportFCLOrderPage"));
const ContainerTransportImportFAKOrderPage = lazy(() => import("./pages/ContainerTransportImportFAKOrderPage"));
const ContainerTransportExportFAKOrderPage = lazy(() => import("./pages/ContainerTransportExportFAKOrderPage"));
const ContainerTransportImportContainerPage = lazy(() => import("./pages/ContainerTransportImportContainerPage"));
const ContainerTransportExportContainerPage = lazy(() => import("./pages/ContainerTransportExportContainerPage"));
const ContainerTransportMoveOrderPage = lazy(() => import("./pages/ContainerTransportMoveOrderPage"));
const ContainerTransportMoveContainerPage = lazy(() => import("./pages/ContainerTransportMoveContainerPage"));
const ContainerTransportOrderPage = lazy(() => import("./pages/ContainerTransportOrderPage"));
const ContainerTransportGeneralFreightOrderPage = lazy(
	() => import("./pages/ContainerTransportGeneralFreightOrderPage")
);
const UserDashboardPage = lazy(() => import("./pages/UserDashboardPage"));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage"));
const UserNewPage = lazy(() => import("./pages/UserNewPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const CustomerDashboardPage = lazy(() => import("./pages/CustomersDashboardPage"));
const CustomerPage = lazy(() => import("./pages/CustomerPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const LocationDetailPage = lazy(() => import("./pages/LocationDetailPage"));
const LocationNewPage = lazy(() => import("./pages/LocationNewPage"));
const CodesDashboardPage = lazy(() => import("./pages/CodesDashboardPage"));
const CodePage = lazy(() => import("./pages/CodePage"));

const {
	containerTransportDashboard,
	users,
	importFCLTransportOrder,
	exportFCLTransportOrder,
	importFAKTransportOrder,
	exportFAKTransportOrder,
	importContainer,
	exportContainer,
	moveOrder,
	moveContainer,
	order,
	customers,
	locations,
	codes,
	generalFreightOrder,
	newsAndUpdates
} = routePermissionMapping;

function ContainerTransportOrderPageWithId() {
	const { id } = useParams();
	return <ContainerTransportOrderPage key={id} />;
}

export default function Router() {
	return (
		<HashRouter>
			<Routes>
				<Route element={<PortalAuthenticatedRoute />}>
					<Route element={<BasePage />}>
						<Route
							element={
								<Suspense
									fallback={
										<Backdrop invisible open>
											<CircularProgress />
										</Backdrop>
									}
								>
									<Outlet />
								</Suspense>
							}
						>
							<Route path="/" element={<HomePage />} />
							<Route
								path={containerTransportDashboard.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={containerTransportDashboard.securityContext}
										securityContextPermission={
											containerTransportDashboard.securityContextPermission
										}
									>
										<ContainerTransportDashboardPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={importFCLTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={importFCLTransportOrder.securityContext}
										securityContextPermission={importFCLTransportOrder.securityContextPermission}
									>
										<ContainerTransportImportFCLOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportFCLTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportFCLTransportOrder.securityContext}
										securityContextPermission={exportFCLTransportOrder.securityContextPermission}
									>
										<ContainerTransportExportFCLOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={importFAKTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={importFAKTransportOrder.securityContext}
										securityContextPermission={importFAKTransportOrder.securityContextPermission}
									>
										<ContainerTransportImportFAKOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportFAKTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportFAKTransportOrder.securityContext}
										securityContextPermission={exportFAKTransportOrder.securityContextPermission}
									>
										<ContainerTransportExportFAKOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={importContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={importContainer.securityContext}
										securityContextPermission={importContainer.securityContextPermission}
									>
										<ContainerTransportImportContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportContainer.securityContext}
										securityContextPermission={exportContainer.securityContextPermission}
									>
										<ContainerTransportExportContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={moveOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={moveOrder.securityContext}
										securityContextPermission={moveOrder.securityContextPermission}
									>
										<ContainerTransportMoveOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={moveContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={moveContainer.securityContext}
										securityContextPermission={moveContainer.securityContextPermission}
									>
										<ContainerTransportMoveContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={generalFreightOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={generalFreightOrder.securityContext}
										securityContextPermission={generalFreightOrder.securityContextPermission}
									>
										<ContainerTransportGeneralFreightOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={order.path}
								element={
									<PortalAuthenticatedRoute permissions={order.permissions || []}>
										<ContainerTransportOrderPage key="new" />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={order.path + "/:id"}
								element={
									<PortalAuthenticatedRoute permissions={order.permissions || []}>
										<ContainerTransportOrderPageWithId />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={newsAndUpdates.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={newsAndUpdates.securityContext}
										securityContextPermission={newsAndUpdates.securityContextPermission}
									>
										<NewsAndUpdatesPage routePrefix={newsAndUpdates.path} />
									</PortalAuthenticatedRoute>
								}
							/>

							<Route
								path={newsAndUpdates.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={newsAndUpdates.securityContext}
										securityContextPermission={newsAndUpdates.securityContextPermission}
									>
										<NewsArticleDetailPage routePrefix={newsAndUpdates.path} />
									</PortalAuthenticatedRoute>
								}
							/>

							<Route
								path={newsAndUpdates.path + "/New"}
								element={
									<PortalAuthenticatedRoute
										securityContext={newsAndUpdates.securityContext}
										securityContextPermission={newsAndUpdates.securityContextPermission}
									>
										<NewsArticleEditorPage routePrefix={newsAndUpdates.path} />
									</PortalAuthenticatedRoute>
								}
							/>

							<Route
								path={newsAndUpdates.path + "/:id/edit"}
								element={
									<PortalAuthenticatedRoute
										securityContext={newsAndUpdates.securityContext}
										securityContextPermission={newsAndUpdates.securityContextPermission}
									>
										<NewsArticleEditorPage routePrefix={newsAndUpdates.path} />
									</PortalAuthenticatedRoute>
								}
							/>

							<Route
								path={users.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={users.securityContext}
										securityContextPermission={users.securityContextPermission}
									/>
								}
							>
								<Route index element={<UserDashboardPage />} />
								<Route path=":id" element={<UserDetailPage />} />

								<Route
									path="new"
									element={
										<PortalAuthenticatedRoute
											securityContext="portal_Users"
											securityContextPermission="Write"
										>
											<UserNewPage />
										</PortalAuthenticatedRoute>
									}
								/>
							</Route>
							<Route
								path={locations.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={locations.securityContext}
										securityContextPermission={locations.securityContextPermission}
									/>
								}
							>
								<Route index element={<LocationPage />} />
								<Route
									path=":id"
									element={
										<PortalAuthenticatedRoute
											securityContext={locations.securityContext}
											securityContextPermission="Web_Locations_Write"
										>
											<LocationDetailPage />
										</PortalAuthenticatedRoute>
									}
								/>
								<Route
									path="New"
									element={
										<PortalAuthenticatedRoute
											securityContext={locations.securityContext}
											securityContextPermission="Web_Locations_Write"
										>
											<LocationNewPage />
										</PortalAuthenticatedRoute>
									}
								/>
							</Route>
							<Route path="/MyAccount" element={<MyAccountPage />} />
							<Route
								path={codes.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={codes.securityContext}
										securityContextPermission={codes.securityContextPermission}
									/>
								}
							>
								<Route index element={<CodesDashboardPage />} />
							</Route>
							<Route
								path="/Code/:id"
								element={
									<PortalAuthenticatedRoute
										securityContext={codes.securityContext}
										securityContextPermission={codes.securityContextPermission}
									/>
								}
							>
								<Route index element={<CodePage />} />
							</Route>
							<Route
								path={customers.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={customers.securityContext}
										securityContextPermission={customers.securityContextPermission}
									>
										<CustomerDashboardPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={"/Customer/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={customers.securityContext}
										securityContextPermission={customers.securityContextPermission}
									>
										<CustomerPage />
									</PortalAuthenticatedRoute>
								}
							/>
						</Route>
					</Route>
				</Route>
				{/* Portal Framework */}
				<Route path="/Login" element={<LoginPage />} />
				<Route path="/ForgotPassword" element={<ForgotPasswordPage />} />
				<Route path="/ChangePassword" element={<ChangePasswordPage />} />
				<Route path="/ResetPassword/:passwordResetToken" element={<ResetPasswordPage />} />
				<Route path="/NotFound" element={<NotFound />} />
				{/* TODO: Remove this when the correct security context is available */}
				<Route path={importFCLTransportOrder.path} element={<ContainerTransportImportFCLOrderPage />} />
				<Route path={exportFCLTransportOrder.path} element={<ContainerTransportExportFCLOrderPage />} />
				<Route path={importFAKTransportOrder.path} element={<ContainerTransportImportFAKOrderPage />} />
				<Route path={exportFAKTransportOrder.path} element={<ContainerTransportExportFAKOrderPage />} />
				<Route path={users.path} element={<UserDashboardPage />} />
				<Route path={users.path + "/:id"} element={<UserDetailPage />} />
				<Route path={users.path + "/new"} element={<UserNewPage />} />
				<Route path="/MyAccount" element={<MyAccountPage />} />
				<Route path="*" element={<NotFound />} />
				{/* Portal Framework */}
			</Routes>
		</HashRouter>
	);
}
